import { useRef, useState, forwardRef, useImperativeHandle } from "react";
import { colors } from "../_constants";

export type DrawingBoardHandle = {
  getImageBlob: () => Promise<Blob | null>;
};

const DrawingBoard = forwardRef<DrawingBoardHandle>(function DrawingBoard(
  _,
  ref
) {
  const [drawingColor, setDrawingColor] = useState("#ff6b6b");
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  // Helper function to get accurate mouse/touch position
  const getMousePos = (canvas: any, e: any) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    // Handle touch events
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  // Drawing functionality for mouse
  const startDrawing = (e: any) => {
    if (!canvasRef.current) return;

    e.preventDefault();
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const pos = getMousePos(canvas, e);
    const ctx = canvas.getContext("2d")!;
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e: any) => {
    if (!isDrawing || !canvasRef.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const pos = getMousePos(canvas, e);
    const ctx = canvas.getContext("2d")!;
    ctx.lineWidth = 4;
    ctx.lineCap = "round";
    ctx.strokeStyle = drawingColor;
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const stopDrawing = (e: any) => {
    if (e) e.preventDefault();
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d")!;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
  // Expose the canvas ref to parent via forwardRef
  useImperativeHandle(ref, () => ({
    getImageBlob: async () => {
      if (!canvasRef.current) return null;

      const canvas = canvasRef.current;
      const tempCanvas = document.createElement("canvas");
      const tempCtx = tempCanvas.getContext("2d");

      // Set same dimensions as original canvas
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;

      // Fill with white background
      tempCtx.fillStyle = "white";
      tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

      // Draw the original canvas content on top
      tempCtx.drawImage(canvas, 0, 0);
      const imageBlob = await new Promise<Blob | null>((resolve) => {
        tempCanvas.toBlob(
          (blob) => {
            resolve(blob);
          },
          "image/png",
          1
        );
      });

      downloadBlob(imageBlob, "drawing.png");

      if (imageBlob && imageBlob.size < 100) return null;

      return imageBlob;
    },
  }));

  return (
    <div className="mb-6 p-4 bg-gradient-to-r from-pink-100 to-purple-100 rounded-2xl">
      {/* Color Picker */}
      <div className="mb-4">
        <h3 className="text-lg font-bold text-purple-800 mb-3 text-center">
          🎨 Choose Your Color! 🎨
        </h3>
        <div className="flex flex-wrap justify-center gap-2 mb-3">
          {colors.map((colorOption) => (
            <button
              key={colorOption.color}
              onClick={() => setDrawingColor(colorOption.color)}
              className={`w-12 h-12 rounded-full border-4 transition-all duration-200 transform hover:scale-110 hover:shadow-lg ${
                drawingColor === colorOption.color
                  ? "border-gray-800 shadow-lg scale-110"
                  : "border-white shadow-md hover:border-gray-400"
              }`}
              style={{ backgroundColor: colorOption.color }}
              title={colorOption.name}
            />
          ))}
        </div>
      </div>

      <canvas
        ref={canvasRef}
        width={500}
        height={300}
        className="w-full max-w-lg mx-auto border-4 border-dashed border-purple-300 rounded-xl bg-white cursor-crosshair block touch-none"
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
        onTouchCancel={stopDrawing}
        style={{ touchAction: "none" }}
      />

      <p className="text-sm text-gray-600 mt-2 text-center text-red-500">
        Note: Please don't apply color inside the images. Draw only the
        outlines.
      </p>
      <div className="flex justify-center gap-2 mt-2">
        <button
          onClick={clearCanvas}
          className="bg-gradient-to-r from-red-400 to-pink-400 text-white px-4 py-2 rounded-lg hover:shadow-lg transform hover:scale-105 transition-all duration-200"
        >
          🗑️ Clear Drawing
        </button>
        <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg shadow-md">
          <span className="text-sm font-semibold text-gray-700">Current:</span>
          <div
            className="w-6 h-6 rounded-full border-2 border-gray-300"
            style={{ backgroundColor: drawingColor }}
          ></div>
        </div>
      </div>
    </div>
  );
});

export default DrawingBoard;
